<script setup lang="ts">
import { computed, reactive, watch } from 'vue';
import CInput from '@/components/shared/CInput';
import CButton from '@/components/shared/CButton';
import {
    type PatternConfig,
    type PatternElement,
    PATTERN_DEFAULTS
} from '@/typings/customilyElements/patternElements';

const props = withDefaults(
    defineProps<{
        element?: PatternElement;
        pattern?: boolean;
        createText?: string;
    }>(),
    { createText: 'Create Pattern from Element' }
);

const emit = defineEmits<{
    (e: 'create-pattern'): void;
    (e: 'pattern-config-change', partial: Partial<PatternConfig>): void;
    (e: 'refresh-fill'): void;
    (e: 'remove-pattern'): void;
}>();

const isPattern = computed(() => !!props.pattern);

const model = reactive({
    spacingX: PATTERN_DEFAULTS.spacingX * 100,
    spacingY: PATTERN_DEFAULTS.spacingY * 100,
    offsetX: PATTERN_DEFAULTS.offsetX * 100,
    offsetY: PATTERN_DEFAULTS.offsetY * 100
});

const toPercent = (fraction: number | undefined) => Math.round((fraction ?? 0) * 100);

const initModel = (element?: PatternElement) => {
    const config = element?.patternConfig || PATTERN_DEFAULTS;
    model.spacingX = toPercent(config.spacingX);
    model.spacingY = toPercent(config.spacingY);
    model.offsetX = toPercent(config.offsetX);
    model.offsetY = toPercent(config.offsetY);
};

watch(
    () => props.element,
    (element) => initModel(element),
    { immediate: true }
);
watch(
    () => props.pattern,
    () => initModel(props.element)
);

const onConfigInput = (key: keyof PatternConfig, value: unknown) => {
    const percent = Number(value);
    if (Number.isNaN(percent)) return;
    (model as Record<string, number>)[key] = percent;
    emit('pattern-config-change', { [key]: percent / 100 } as Partial<PatternConfig>);
};
</script>

<template>
    <div class="pattern-wrapper">
        <template v-if="!isPattern">
            <c-button variant="secondary" :text="createText" @click="emit('create-pattern')" />
        </template>

        <template v-else>
            <div class="section-title">Spacing</div>
            <hr class="section-divider" />
            <div class="row">
                <div class="col-6">
                    <c-input
                        :value="model.spacingX"
                        type="number"
                        label="Horizontal (%)"
                        :step="1"
                        @input="onConfigInput('spacingX', $event)"
                    />
                </div>
                <div class="col-6">
                    <c-input
                        :value="model.spacingY"
                        type="number"
                        label="Vertical (%)"
                        :step="1"
                        @input="onConfigInput('spacingY', $event)"
                    />
                </div>
            </div>

            <div class="section-title">Offset</div>
            <hr class="section-divider" />
            <div class="row">
                <div class="col-6">
                    <c-input
                        :value="model.offsetX"
                        type="number"
                        label="Horizontal (%)"
                        :step="1"
                        @input="onConfigInput('offsetX', $event)"
                    />
                </div>
                <div class="col-6">
                    <c-input
                        :value="model.offsetY"
                        type="number"
                        label="Vertical (%)"
                        :step="1"
                        @input="onConfigInput('offsetY', $event)"
                    />
                </div>
            </div>

            <div class="pattern-actions">
                <c-button
                    class="w-50"
                    variant="secondary"
                    text="Refresh to Fill Page"
                    :icon="['far', 'arrow-rotate-left']"
                    @click="emit('refresh-fill')"
                />
                <c-button
                    class="w-50"
                    variant="secondary"
                    text="Remove"
                    :icon="['far', 'trash']"
                    @click="emit('remove-pattern')"
                />
            </div>
        </template>
    </div>
</template>

<style lang="scss" scoped>
.pattern-wrapper {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;

    .section-title {
        font-weight: 400;
        font-size: 0.875rem;
    }

    .section-divider {
        margin: 0;
    }

    .pattern-actions {
        display: flex;
        justify-content: space-between;
        gap: 0.5rem;
        margin-top: 0.5rem;
    }
}
</style>
